/**
 * Phaser department navigation (FPV & Drones / 3D Printing / PC).
 *
 * State lives on <html data-phaser-dept>, set before first paint by the inline
 * script in snippets/phaser-department-state.liquid. CSS shows the matching
 * [data-pdn-panel] menus and styles the active selector segment from that
 * attribute. This module handles switching: it persists the choice, keeps
 * aria-pressed in sync on every selector copy, and closes menus that belonged
 * to the previous department.
 *
 * Listeners are attached to document once per page (module scripts run once),
 * so Theme Editor section reloads never stack duplicate handlers.
 *
 * Also adds Escape-to-close and live aria-expanded to the CSS hover dropdowns.
 */

const STORAGE_KEY = 'phaserStoreMode';
const EDITOR_STORAGE_KEY = 'phaserStoreModeEditor';
const DEFAULT_MODE = 'fpv';
const root = document.documentElement;

/** @returns {Storage | null} */
function getStore() {
  try {
    return window.Shopify?.designMode ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
}

function storageKey() {
  return window.Shopify?.designMode ? EDITOR_STORAGE_KEY : STORAGE_KEY;
}

/**
 * Departments the current header can show. Read from the markup so a Theme
 * Editor reload that removes a menu is picked up immediately.
 * @returns {string[]}
 */
function availableModes() {
  const source = document.querySelector('[data-pdn-available]');
  const list = source?.getAttribute('data-pdn-available') || root.getAttribute('data-phaser-dept-available') || '';
  return list.split(',').filter(Boolean);
}

function currentMode() {
  return root.getAttribute('data-phaser-dept') || DEFAULT_MODE;
}

/** Keep every selector copy's pressed state in line with the shown menu. */
function syncPressed() {
  const mode = currentMode();
  for (const button of document.querySelectorAll('[data-pdn-set]')) {
    button.setAttribute('aria-pressed', String(button.getAttribute('data-pdn-set') === mode));
  }
}

/**
 * Reset anything open inside panels that are no longer shown, so the shopper
 * is never left inside an obsolete dropdown or drawer submenu.
 * @param {string} mode - The department now shown
 */
function closeStaleMenus(mode) {
  for (const panel of document.querySelectorAll('[data-pdn-panel]')) {
    if (panel.getAttribute('data-pdn-panel') === mode) continue;

    // Drawer submenus (header-drawer.js resets the same way).
    for (const details of panel.querySelectorAll('details[open], details.menu-open')) {
      details.classList.remove('menu-open');
      details.removeAttribute('open');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    }

    // Desktop dropdowns open through :focus-within; drop focus out of them.
    if (panel.contains(document.activeElement) && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  document.querySelector('.menu-drawer--has-submenu-opened')?.classList.remove('menu-drawer--has-submenu-opened');
}

/**
 * Show a department's menus and remember the choice.
 * @param {string} mode
 * @param {string} [origin] - 'selector', 'intro' or 'editor'; passed on in the event
 */
function setMode(mode, origin = 'selector') {
  const available = availableModes();
  if (!available.includes(mode)) return;

  closeStaleMenus(mode);
  root.setAttribute('data-phaser-dept', mode);
  syncPressed();

  try {
    getStore()?.setItem(storageKey(), mode);
  } catch {
    // Storage full or blocked: the switch still works for this page view.
  }

  document.dispatchEvent(new CustomEvent('phaser:department-change', { detail: { mode, origin } }));
}

/**
 * Re-check state against the current markup (first load, section reloads).
 * A department that is no longer available falls back to FPV and is not kept.
 */
function validate() {
  const available = availableModes();
  if (available.length === 0) return;

  const mode = currentMode();
  if (!available.includes(mode)) {
    root.setAttribute('data-phaser-dept', available.includes(DEFAULT_MODE) ? DEFAULT_MODE : available[0]);
    try {
      getStore()?.removeItem(storageKey());
    } catch {
      // Ignore storage failures.
    }
  }

  root.setAttribute('data-phaser-dept-available', available.join(','));
  syncPressed();
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-pdn-set]') : null;
  if (!(target instanceof HTMLButtonElement) || target.disabled) return;

  setMode(target.getAttribute('data-pdn-set') || DEFAULT_MODE, target.closest('.pdn-intro') ? 'intro' : 'selector');
});

// ── Desktop dropdowns: Escape to close, live aria-expanded ──────────────────

/**
 * Open means hovered or focused, not dismissed, and the panel is on the page
 * (panels arrive a moment after load, see custom-phaser-header-lazy.js).
 * @param {Element} item
 */
function isOpen(item) {
  return (
    !item.classList.contains('cdm-nav__item--dismissed') &&
    item.matches(':hover, :focus-within') &&
    !!item.querySelector(':scope > .cdm-dropdown')
  );
}

const EDGE_GAP = 16;

/**
 * Nudge an open dropdown back inside the viewport. Panels centre on their
 * item, so items near either edge could otherwise push them off screen.
 * @param {Element} item
 */
function keepInViewport(item) {
  const panel = item.querySelector(':scope > .cdm-dropdown');
  if (!(panel instanceof HTMLElement)) return;

  // Work from layout values the open animation doesn't touch: the panel is
  // centred on its containing block (the item, or the nav for wide megas).
  const width = panel.offsetWidth;
  const anchor = panel.offsetParent;
  if (!width || !(anchor instanceof HTMLElement)) return;
  const anchorRect = anchor.getBoundingClientRect();
  const left = anchorRect.left + anchorRect.width / 2 - width / 2;
  const right = left + width;

  // The page can scroll inside .page-wrapper, which is narrower than the window.
  const wrapper = document.querySelector('.page-wrapper');
  const wrapperRect = wrapper?.getBoundingClientRect();
  const limitRight = Math.min(
    window.innerWidth,
    document.documentElement.clientWidth,
    wrapper && wrapperRect ? wrapperRect.left + wrapper.clientWidth : Infinity,
  );

  const current = parseFloat(panel.style.getPropertyValue('--cdm-shift')) || 0;
  let shift = 0;
  if (right > limitRight - EDGE_GAP) shift = limitRight - EDGE_GAP - right;
  if (left + shift < EDGE_GAP) shift = EDGE_GAP - left;

  if (Math.round(shift) !== Math.round(current)) panel.style.setProperty('--cdm-shift', `${Math.round(shift)}px`);
}

/**
 * Runs after every pointer and focus event in the nav, so it stays cheap:
 * aria-expanded is only written when it changes, and dropdowns the Exploded
 * Build owns (.qd-own, hidden by concept-quad.js) are never measured. Measuring
 * one forced a full layout on each hover, ~140ms a hover on a slow laptop.
 */
function syncExpanded() {
  for (const item of document.querySelectorAll('.cdm-nav__item--has-dropdown')) {
    // Phaser: the concept owns its expanded state even before native panels exist.
    if (item.classList.contains('qd-own')) continue;
    const open = isOpen(item);
    const link = item.querySelector(':scope > .cdm-nav__link');
    if (link && link.getAttribute('aria-expanded') !== String(open)) link.setAttribute('aria-expanded', String(open));
    if (open) keepInViewport(item);
  }
}

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const item = event.target instanceof Element ? event.target.closest('.cdm-nav__item--has-dropdown') : null;
  if (!item) return;

  item.classList.add('cdm-nav__item--dismissed');
  const trigger = item.querySelector(':scope > .cdm-nav__link');
  if (trigger instanceof HTMLElement) trigger.focus();
  syncExpanded();
});

/** @param {Event} event */
function clearDismissed(event) {
  const item = event.target instanceof Element ? event.target.closest('.cdm-nav__item--dismissed') : null;
  if (!item) return;
  if (event instanceof FocusEvent && event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;
  item.classList.remove('cdm-nav__item--dismissed');
}

document.addEventListener('focusout', clearDismissed);
document.addEventListener('pointerout', (event) => {
  const item = event.target instanceof Element ? event.target.closest('.cdm-nav__item--dismissed') : null;
  if (item && !(event.relatedTarget instanceof Node && item.contains(event.relatedTarget))) {
    item.classList.remove('cdm-nav__item--dismissed');
  }
});

// Trigger clicks on heading buttons should open (focus) rather than do nothing
// in browsers that do not focus buttons on click (Safari).
document.addEventListener('click', (event) => {
  const heading = event.target instanceof Element ? event.target.closest('.cdm-nav__link--heading') : null;
  if (heading instanceof HTMLElement) heading.focus();
});

let expandedQueued = false;
const inNav = (node) => node instanceof Element && !!node.closest('.cdm-nav');
/** @param {Event} [event] Only events that start or end inside a nav can open or close a dropdown. */
function queueSyncExpanded(event) {
  if (event && !inNav(event.target) && !inNav(/** @type {any} */ (event).relatedTarget)) return;
  // Phaser: crossing text, chevrons and links inside one dropdown does not
  // change its open state. Avoid re-scanning every department for those events.
  if (event && (event.type.startsWith('pointer') || event.type.startsWith('focus'))) {
    const from = event.target instanceof Element ? event.target.closest('.cdm-nav__item') : null;
    const related = /** @type {FocusEvent | PointerEvent} */ (event).relatedTarget;
    if (from && related instanceof Node && from.contains(related)) return;
  }
  if (expandedQueued) return;
  expandedQueued = true;
  // A timer rather than requestAnimationFrame: rAF never fires in background
  // tabs, and the measurement forces layout anyway.
  setTimeout(() => {
    expandedQueued = false;
    syncExpanded();
  }, 0);
}

for (const type of ['focusin', 'focusout', 'pointerover', 'pointerout']) {
  document.addEventListener(type, queueSyncExpanded, { passive: true });
}

// ── Lifecycle ───────────────────────────────────────────────────────────────

document.addEventListener('shopify:section:load', () => {
  validate();
  syncExpanded();
});

// Dropdown panels just arrived: an item already hovered or focused is now open.
document.addEventListener('phaser:header-hydrated', () => {
  syncPressed();
  syncExpanded();
});

// Theme Editor: selecting a section limited to another department switches the
// preview to it, so the section being edited is never hidden.
document.addEventListener('shopify:section:select', (event) => {
  const id = event instanceof CustomEvent ? event.detail?.sectionId : null;
  if (!id) return;
  const marker = document.querySelector(`#shopify-section-${CSS.escape(id)} > .pdn-only`);
  const mode = marker?.getAttribute('data-pdn-only');
  if (mode && mode !== currentMode()) setMode(mode, 'editor');
});

validate();
